import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClockIcon } from '@/components/ui/icons';
import { useArticles } from '@/hooks/useSupabase';

export function ArticlesSection() {
  const { t, i18n } = useTranslation('home');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';

  // Fetch latest 4 published articles from Supabase (filtered by current language)
  const { data: articles, isLoading } = useArticles({
    limit: 4,
    lang: currentLocale,
  });

  if (isLoading) {
    return (
      <section className="py-16 bg-aspexit-gray-light">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-aspexit-green border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (!articles || articles.length === 0) {
    return null;
  }
  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-aspexit-black">
            {t('articles.title')}
          </h2>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {articles.map((article) => (
            <Card
              key={article.id}
              className="h-full overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 group"
            >
              <Link to={`/${currentLocale}/blog/${article.slug}`} className="flex flex-col h-full">
                {/* Featured Image */}
                {article.featured_image ? (
                  <div className="h-48 overflow-hidden flex-shrink-0">
                    <img
                      src={article.featured_image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-aspexit-green/20 to-aspexit-orange/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-aspexit-gray">Image</span>
                  </div>
                )}

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  <Badge
                    variant="outline"
                    className="mb-3 text-aspexit-green border-aspexit-green w-fit"
                  >
                    {article.category}
                  </Badge>

                  <h3 className="text-lg font-heading font-semibold text-aspexit-black mb-3 group-hover:text-aspexit-green transition-colors line-clamp-2 min-h-[3.5rem]">
                    {article.title}
                  </h3>

                  <p className="text-sm text-aspexit-gray mb-4 line-clamp-3 flex-1 min-h-[4.5rem]">
                    {article.excerpt?.replace(/^Partagez cet article\s*!\s*/i, '')}
                  </p>

                  {/* Meta - pushed to bottom */}
                  <div className="mt-auto">
                    <div className="flex items-center justify-between text-sm text-aspexit-gray mb-4">
                      <span>
                        {new Date(
                          article.published_at || article.created_at
                        ).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <ClockIcon className="w-4 h-4" size={16} />
                        {article.reading_time} min
                      </span>
                    </div>

                    <span className="text-aspexit-green text-sm font-medium group-hover:underline">
                      {t('articles.readMore')}
                    </span>
                  </div>
                </div>
              </Link>
            </Card>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Button
            asChild
            size="lg"
            className="bg-aspexit-green hover:bg-aspexit-green-dark"
          >
            <Link to={`/${currentLocale}/blog`}>{t('articles.viewAll')}</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
